import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import Layout from '@/layouts';
import { NextPageWithLayout } from '../_app';
import { fetchInsLoadingDemoWith } from '@/lib/service';
import { page3FaqJsonLd } from '@/lib/jsonld';
import { getFaqQuestions } from '@/components/WhoUnFollow/FaqData';
import { AllSupportLanguages } from '@/shared/language';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const WhoUnFollow = dynamic(() => import('@/components/WhoUnFollow'), {
  ssr: true,
});

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;
  const path = '/instagram-unfollowers-tracker';

  const [localeRes, tdkRes, dataResRaw] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchInsLoadingDemoWith(path, locale, '', '').catch((): null => null),
  ]);

  // INFO: 如果当前语言没有配置数据，fallback 到英文
  let dataRes = dataResRaw;
  if (!dataRes?.data && locale !== 'en') {
    dataRes = await fetchInsLoadingDemoWith(path, 'en', '', '').catch((): null => null);
  }

  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;

  // 创建一个简单的翻译函数，用于在服务端获取FAQ数据
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const t = (key: string) => tempData[key] || key;

  // 获取FAQ问题数据用于page3FaqJsonLd
  const faqQuestions = getFaqQuestions(t);

  const jsonLd = page3FaqJsonLd(
    {
      title: 'Instagram Unfollowers Tracker',
      desc: tdkRes?.data?.description ?? '',
      url: canonicalUrl(SITE_HOST, path, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    faqQuestions, // 正确传递FAQ问题数组
    {
      name: 'Instagram Unfollowers Tracker — DolphinRadar',
      url: 'https://www.dolphinradar.com/instagram-unfollowers-tracker',
      applicationCategory: 'Instagram Follower Tracker',
      description:
        "See who unfollowed you, who doesn't follow back, and your mutual followers using Dolphin Radar's Instagram Unfollowers Tracker.",
      featureList: [
        'Track Who Unfollowed You',
        "Identify Who's Not Following Back",
        'Discover Mutual Followers',
        'No Instagram Login Required',
      ],
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, path, locale),
      locale,
      title: tdkRes?.data?.title ?? '',
      description: tdkRes?.data?.description ?? '',
      data: dataRes?.data ?? null, // 确保 undefined 转为 null，避免序列化错误
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('instagram-unfollowers-tracker'),
      __isr_page_name: 'instagram-unfollowers-tracker',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('instagram-unfollowers-tracker'),
  };
};

const IndexPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <WhoUnFollow {...props} />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

IndexPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
          {AllSupportLanguages.map((lang) => (
            <link
              key={lang.code}
              rel="alternate"
              hrefLang={lang.code}
              href={`https://www.dolphinradar.com/${lang.code}/instagram-unfollowers-tracker`}
            />
          ))}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default IndexPage;
